import logger from "src/utils/logger";
import { socketInprogressServiceStatus } from "src/enums/socketInprogressServiceStatus";
import { workflowStepIterator } from "src/services/workflowStepIterator";
class WebSocketService {
    static instance = null;
    constructor() {
        if (WebSocketService.instance) {
            throw new Error("WebSocketService is a singleton. Use getInstance() to access it.");
        }
        this.socket = null;
        this.retryCount = 0;
        this.maxRetries = 6;
        this.retryTimeout = null;
    }
    static getInstance() {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }
        return WebSocketService.instance;
    }
    initialize(url, token, applicationStore, navigate) {
        if (this.socket) {
            this.close();
        }
        this.url = url;
        this.token = token;
        this.applicationStore = applicationStore;
        this.navigate = navigate;
        this.connect();
    }
    connect() {
        this.socket = new WebSocket(`${this.url}/event_listener/?token=${this.token}`);
        this.socket.onopen = () => {
            logger.log("WebSocket connected", this.socket);
            this.retryCount = 0;
            if (this.retryTimeout) {
                clearTimeout(this.retryTimeout);
            }
        };
        this.socket.onmessage = async (event) => {
            if (event.data === "ping") {
                this.socket.send("pong");
                return;
            }

            const splittedData = String(event.data).split("-");
            logger.log("Message received from WebSocket:", event.data, splittedData);

            const serviceName = splittedData[0];
            const statusCode = splittedData[1];
            const encodedPayload = splittedData.slice(2).join("-");

            let reason = null;
            if (encodedPayload) {
                try {
                    const jsonString = new TextDecoder("utf-8").decode(
                        Uint8Array.from(atob(encodedPayload), c => c.charCodeAt(0))
                    );
                    const decodedJson = JSON.parse(jsonString);
                    reason = decodedJson?.reason ?? null;
                } catch (error) {
                    logger.warn("Reason parse failed", { encodedPayload, error });
                }
            }

            const getCurrentPath = () => {
                try {
                    if (window.top !== window.self) {
                        return window.top.location.pathname;
                    }
                    return window.location.pathname;
                } catch (error) {
                    return window.location.pathname;
                }
            };

            if (serviceName === "documentVerification" && getCurrentPath() === "/processing") {
                try {
                    const response = await workflowStepIterator({
                        access_token: this.applicationStore.access_token,
                    });
                    this.applicationStore.setWorkflowNextServiceResponse(response.data);
                } catch (error) {
                    logger.error("workflowStepIterator failed for documentVerification:", error);
                }
                return;
            }
            //TODO : If task KYXFE-1784 is successful, this text will be deleted
            if (serviceName === "account") {
                if (statusCode === "1") {
                    this.applicationStore.setAccountStatus('success');
                } else if (statusCode === "0") {
                    this.applicationStore.setAccountStatus('failed');
                }
                return;
            }

            if (serviceName === "inquiry") {
                if (statusCode === "1") {
                    this.applicationStore.setInquiryStatus('approved');
                } else if (statusCode === "0") {
                    this.applicationStore.setInquiryStatus('declined');
                } else {
                    this.applicationStore.setInquiryStatus("inprogress");
                }
                return;
            }

            if (serviceName === "pep") {
                if (statusCode === "started") {
                    this.applicationStore.setPepStatus('started');
                    if (getCurrentPath() !== "/processing-review") {
                        this.navigate("/processing-review", { replace: true });
                    }
                    return;
                } else if (statusCode === "1") {
                    this.applicationStore.setPepStatus('success');
                } else if (statusCode === "0") {
                    this.applicationStore.setPepStatus('failed');
                }
                return;
            }

            if (statusCode === "0") {
                this.applicationStore.setInprogressServiceStatus(
                    serviceName,
                    socketInprogressServiceStatus.FAILED,
                    reason
                );
            } else if (statusCode === "1") {
                this.applicationStore.setInprogressServiceStatus(
                    serviceName,
                    socketInprogressServiceStatus.COMPLETED,
                    reason
                );
            } else if (statusCode === "2") {
                this.applicationStore.setInprogressServiceStatus(
                    serviceName,
                    socketInprogressServiceStatus.RETRY,
                    reason
                );
            }
        };
        this.socket.onerror = (error) => {
            logger.error("WebSocket error:", error);
        };
        this.socket.onclose = (event) => {
            logger.log("WebSocket disconnected:", event);
            this.handleReconnection();
        };
    }
    handleReconnection() {
        if (this.retryCount >= this.maxRetries) {
            logger.error("Maximum reconnection attempts reached. Giving up.");
            return;
        }
        const retryDelay = (this.retryCount + 1) * 5000;
        this.retryTimeout = setTimeout(() => {
            logger.log(`Reconnection attempt #${this.retryCount + 1}`);
            this.retryCount++;
            this.connect();
        }, retryDelay);
    }
    close() {
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }
        this.retryCount = 0;
    }
}
export default WebSocketService;
